import { useState, useEffect, useCallback, useMemo } from 'react';
import { motion } from 'framer-motion';
import {
  TrendingUp,
  TrendingDown,
  Activity,
  Clock,
  Target,
  Zap,
  BarChart3,
  Download,
  Loader2,
  Minus,
  Brain,
  Sparkles,
  Compass,
} from 'lucide-react';
import { api, handleApiError } from '@/services/client';

type TimeRange = 'day' | 'week' | 'month';

interface ProductivityStats {
  tabs_opened: number;
  tabs_closed: number;
  sessions_created: number;
  total_time_seconds: number;
  focus_time_seconds: number;
  productivity_score: number;
  cognitive_dispersion: number;
  cognitive_state: string;
  research_synthesis: string;
  top_recommendation: string;
  category_breakdown: Record<string, number>;
  apps_generated: number;
}

interface TimelineEntry {
  date: string;
  label?: string;
  productivity_score: number;
  tabs_opened: number;
  focus_time_seconds: number;
}

interface TrendCard {
  label: string;
  change: number;
  up: boolean;
  direction: 'up' | 'down' | 'flat';
}

const EMPTY_STATS: ProductivityStats = {
  tabs_opened: 0,
  tabs_closed: 0,
  sessions_created: 0,
  total_time_seconds: 0,
  focus_time_seconds: 0,
  productivity_score: 0,
  cognitive_dispersion: 0.2,
  cognitive_state: 'deep_focus',
  research_synthesis: '',
  top_recommendation: '',
  category_breakdown: {},
  apps_generated: 0,
};

const CHART_HEIGHT_PX = 200;

function num(raw: unknown): number {
  if (typeof raw === 'number' && !Number.isNaN(raw)) return raw;
  if (typeof raw === 'string') {
    const n = parseFloat(raw);
    return Number.isNaN(n) ? 0 : n;
  }
  return 0;
}

function formatDurationFromSeconds(seconds: number): string {
  const safe = Number.isFinite(seconds) && seconds > 0 ? Math.round(seconds) : 0;
  const hours = Math.floor(safe / 3600);
  const minutes = Math.floor((safe % 3600) / 60);
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
}

function getScoreColor(score: number) {
  if (score >= 80) return 'text-success-400';
  if (score >= 60) return 'text-yellow-400';
  return 'text-red-400';
}

function getScoreBarClass(score: number) {
  if (score >= 80) return 'bg-gradient-to-t from-emerald-500 to-emerald-400';
  if (score >= 60) return 'bg-gradient-to-t from-amber-500 to-amber-400';
  if (score > 0) return 'bg-gradient-to-t from-rose-500 to-rose-400';
  return 'bg-khiimori-bg-elevated/30';
}

function topCategory(breakdown: Record<string, number>): string {
  const entries = Object.entries(breakdown).sort(([, a], [, b]) => b - a);
  if (entries.length === 0) return '—';
  const [key] = entries[0];
  return key.charAt(0).toUpperCase() + key.slice(1);
}

function mapTrendRow(raw: Record<string, unknown>): TrendCard {
  const direction = String(raw.direction ?? 'flat') as TrendCard['direction'];
  return {
    label: String(raw.label ?? ''),
    change: num(raw.change),
    up: Boolean(raw.up),
    direction,
  };
}

export const ProductivityInsightsPage = () => {
  const [stats, setStats] = useState<ProductivityStats>(EMPTY_STATS);
  const [timeline, setTimeline] = useState<TimelineEntry[]>([]);
  const [trends, setTrends] = useState<TrendCard[]>([]);
  const [goalsCompleted, setGoalsCompleted] = useState(0);
  const [timeRange, setTimeRange] = useState<TimeRange>('week');
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [statsRes, timelineRes, trendsRes, goalsRes] = await Promise.allSettled([
        api.get('/productivity/stats', { params: { period: timeRange } }),
        api.get('/productivity/timeline', { params: { period: timeRange } }),
        api.get('/productivity/trends', { params: { period: timeRange } }),
        api.get('/goals', { params: { status: 'completed' } }),
      ]);

      if (statsRes.status === 'fulfilled') {
        const raw = statsRes.value.data ?? {};
        setStats({
          tabs_opened: num(raw.tabs_opened ?? raw.tabs_managed),
          tabs_closed: num(raw.tabs_closed ?? raw.duplicates_closed),
          sessions_created: num(raw.sessions_created ?? raw.sessions),
          total_time_seconds: num(raw.total_time_seconds),
          focus_time_seconds: num(raw.focus_time_seconds),
          productivity_score: num(raw.productivity_score),
          cognitive_dispersion: num(raw.cognitive_dispersion ?? 0.2),
          cognitive_state: String(raw.cognitive_state ?? 'deep_focus'),
          research_synthesis: String(raw.research_synthesis ?? ''),
          top_recommendation: String(raw.top_recommendation ?? ''),
          category_breakdown: raw.category_breakdown ?? {},
          apps_generated: num(raw.apps_generated ?? raw.gen_tabs_created),
        });
      } else {
        setStats(EMPTY_STATS);
        setError(handleApiError(statsRes.reason));
      }

      if (timelineRes.status === 'fulfilled') {
        const rows = timelineRes.value.data?.timeline;
        setTimeline(
          Array.isArray(rows)
            ? rows.map((row: Record<string, unknown>, i) => ({
                date: String(row.date ?? row.label ?? i),
                label: row.label ? String(row.label) : undefined,
                productivity_score: num(row.productivity_score),
                tabs_opened: num(row.tabs_opened ?? row.value),
                focus_time_seconds: num(row.focus_time_seconds),
              }))
            : [],
        );
      } else {
        setTimeline([]);
      }

      if (trendsRes.status === 'fulfilled') {
        const rows = trendsRes.value.data?.trends;
        setTrends(Array.isArray(rows) ? rows.map((r: Record<string, unknown>) => mapTrendRow(r)) : []);
      } else {
        setTrends([]);
      }

      if (goalsRes.status === 'fulfilled') {
        const goals = goalsRes.value.data?.goals;
        setGoalsCompleted(Array.isArray(goals) ? goals.length : 0);
      } else {
        setGoalsCompleted(0);
      }
    } catch (err) {
      setError(handleApiError(err));
      setStats(EMPTY_STATS);
      setTimeline([]);
      setTrends([]);
      setGoalsCompleted(0);
    } finally {
      setLoading(false);
    }
  }, [timeRange]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const exportReport = async () => {
    setExporting(true);
    setError(null);
    try {
      const res = await api.get('/analytics/export', {
        params: { period: timeRange, format: 'csv' },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `productivity_${timeRange}_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setExporting(false);
    }
  };

  const chartMetrics = useMemo(() => {
    if (timeline.length === 0) return [];
    return timeline.map((t) => ({
      date: t.date,
      label: t.label ?? t.date,
      score: t.productivity_score,
      focusSeconds: t.focus_time_seconds,
    }));
  }, [timeline]);

  const totalFocusSeconds = useMemo(() => {
    const fromTimeline = chartMetrics.reduce((sum, m) => sum + m.focusSeconds, 0);
    if (fromTimeline > 0) return fromTimeline;
    return stats.focus_time_seconds;
  }, [chartMetrics, stats.focus_time_seconds]);

  const maxScore = useMemo(
    () => Math.max(1, ...chartMetrics.map((m) => m.score)),
    [chartMetrics],
  );

  const averageScore = useMemo(() => {
    const scores = chartMetrics.map((m) => m.score).filter((s) => s > 0);
    if (scores.length > 0) {
      return Math.round(scores.reduce((a, b) => a + b, 0) / scores.length);
    }
    return stats.productivity_score;
  }, [chartMetrics, stats.productivity_score]);

  const periodReport = useMemo(() => ({
    totalFocusSeconds,
    averageScore,
    topCategory: topCategory(stats.category_breakdown),
  }), [totalFocusSeconds, averageScore, stats.category_breakdown]);

  const activeFocusDays = useMemo(
    () => chartMetrics.filter((m) => m.focusSeconds > 0),
    [chartMetrics],
  );

  const avgDailyFocusSeconds = useMemo(() => {
    if (activeFocusDays.length === 0) return 0;
    return Math.round(
      activeFocusDays.reduce((sum, m) => sum + m.focusSeconds, 0) / activeFocusDays.length,
    );
  }, [activeFocusDays]);

  const focusChartMetrics = useMemo(
    () => (activeFocusDays.length > 0 ? activeFocusDays : chartMetrics),
    [activeFocusDays, chartMetrics],
  );

  const maxFocusSeconds = useMemo(
    () => Math.max(1, ...focusChartMetrics.map((m) => m.focusSeconds)),
    [focusChartMetrics],
  );

  const trendIcon = (trend: TrendCard) => {
    if (trend.direction === 'flat' || trend.change === 0) {
      return <Minus className="w-4 h-4 text-khiimori-text-muted" />;
    }
    return trend.up
      ? <TrendingUp className="w-4 h-4 text-success-400" />
      : <TrendingDown className="w-4 h-4 text-red-400" />;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-8 w-8 animate-spin text-primary-400" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {error && (
        <div className="rounded-lg border border-red-500/40 bg-red-500/10 px-4 py-2 text-sm text-red-300">
          {error}
        </div>
      )}

      {/* Toolbar + KPIs */}
      <motion.div
        initial={{ opacity: 0, y: -12 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-khiimori-bg-elevated/5 backdrop-blur-xl rounded-xl shadow-lg p-5 border border-khiimori-border/10"
      >
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div className="flex flex-wrap items-center gap-2">
            {(['day', 'week', 'month'] as const).map((range) => (
              <motion.button
                key={range}
                type="button"
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                onClick={() => setTimeRange(range)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                  timeRange === range
                    ? 'bg-gradient-primary text-white shadow-md'
                    : 'bg-khiimori-bg-elevated/10 text-khiimori-text-secondary hover:bg-khiimori-bg-elevated/20'
                }`}
              >
                {range.charAt(0).toUpperCase() + range.slice(1)}
              </motion.button>
            ))}
          </div>

          <motion.button
            type="button"
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
            disabled={exporting}
            onClick={() => void exportReport()}
            className="px-3 py-1.5 bg-gradient-primary text-white rounded-lg text-sm font-medium shadow-md flex items-center gap-2 disabled:opacity-60"
          >
            {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            Export
          </motion.button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3">
          <div className="bg-purple-500/20 rounded-xl p-3">
            <div className="flex items-center gap-1.5 mb-1">
              <Target className="w-3.5 h-3.5 text-purple-400" />
              <span className="text-purple-400 text-xs font-medium">Average Score</span>
            </div>
            <div className="text-2xl font-bold text-purple-300">{averageScore}</div>
            <div className="text-xs text-purple-400/80">out of 100</div>
          </div>
          <div className="bg-primary-500/20 rounded-xl p-3">
            <div className="flex items-center gap-1.5 mb-1">
              <Clock className="w-3.5 h-3.5 text-primary-400" />
              <span className="text-primary-400 text-xs font-medium">Total Focus</span>
            </div>
            <div className="text-2xl font-bold text-primary-300">
              {formatDurationFromSeconds(totalFocusSeconds)}
            </div>
            <div className="text-xs text-primary-400/80">
              {avgDailyFocusSeconds > 0
                ? `~${formatDurationFromSeconds(avgDailyFocusSeconds)} / active day`
                : 'this period'}
            </div>
          </div>
          <div className="bg-success-500/20 rounded-xl p-3">
            <div className="flex items-center gap-1.5 mb-1">
              <Activity className="w-3.5 h-3.5 text-success-400" />
              <span className="text-success-400 text-xs font-medium">Tabs Closed</span>
            </div>
            <div className="text-2xl font-bold text-success-300">{stats.tabs_closed}</div>
            <div className="text-xs text-success-400/80">organized</div>
          </div>
          <div className="bg-orange-500/20 rounded-xl p-3">
            <div className="flex items-center gap-1.5 mb-1">
              <Zap className="w-3.5 h-3.5 text-orange-400" />
              <span className="text-orange-400 text-xs font-medium">Goals Done</span>
            </div>
            <div className="text-2xl font-bold text-orange-300">{goalsCompleted}</div>
            <div className="text-xs text-orange-400/80">completed</div>
          </div>
          <div className="bg-teal-500/20 rounded-xl p-3 col-span-2 md:col-span-1">
            <div className="flex items-center gap-1.5 mb-1">
              <BarChart3 className="w-3.5 h-3.5 text-teal-400" />
              <span className="text-teal-400 text-xs font-medium">Top Category</span>
            </div>
            <div className="text-2xl font-bold text-teal-300 truncate">{periodReport.topCategory}</div>
            <div className="text-xs text-teal-400/80">most active</div>
          </div>
        </div>

        {/* AI Cognitive State & Research Synthesis Banner */}
        <div className="mt-4 pt-4 border-t border-khiimori-border/10 grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="bg-gradient-to-r from-violet-600/15 to-indigo-600/15 border border-violet-500/30 rounded-xl p-3.5 flex items-start gap-3">
            <div className="p-2 rounded-lg bg-violet-500/20 text-violet-300 shrink-0">
              <Brain className="w-5 h-5" />
            </div>
            <div>
              <div className="text-xs font-semibold uppercase tracking-wider text-violet-300">Cognitive Load & Dispersion</div>
              <div className="text-sm font-bold text-khiimori-text-primary capitalize mt-0.5">
                {stats.cognitive_state.replace('_', ' ')} ({(stats.cognitive_dispersion * 100).toFixed(0)}% dispersion)
              </div>
              <div className="text-xs text-khiimori-text-secondary mt-1">
                {stats.cognitive_dispersion < 0.4
                  ? 'Strong topic coherence. Minimal context switching across open tabs.'
                  : stats.cognitive_dispersion < 0.7
                  ? 'Balanced exploration across related research domains.'
                  : 'High multi-tasking and fragmentation detected. Consider stashing tangents.'}
              </div>
            </div>
          </div>

          <div className="md:col-span-2 bg-gradient-to-r from-cyan-600/15 to-blue-600/15 border border-cyan-500/30 rounded-xl p-3.5 flex items-start gap-3">
            <div className="p-2 rounded-lg bg-cyan-500/20 text-cyan-300 shrink-0">
              <Sparkles className="w-5 h-5" />
            </div>
            <div className="flex-1">
              <div className="text-xs font-semibold uppercase tracking-wider text-cyan-300 flex items-center justify-between">
                <span>AI Research Synthesis</span>
                <span className="text-[10px] text-cyan-400/80 font-normal">Active Session Cluster</span>
              </div>
              <div className="text-xs text-khiimori-text-primary font-medium mt-1">
                {stats.research_synthesis || 'Analyzing active tab research patterns and semantic topics...'}
              </div>
              {stats.top_recommendation && (
                <div className="text-xs text-cyan-300/90 mt-1.5 flex items-center gap-1.5 font-medium">
                  <Compass className="w-3.5 h-3.5 shrink-0" />
                  <span className="truncate">Directive: {stats.top_recommendation}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </motion.div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <motion.div
          initial={{ opacity: 0, x: -12 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.05 }}
          className="bg-khiimori-bg-elevated/5 backdrop-blur-xl rounded-xl shadow-md p-5 border border-khiimori-border/10 flex flex-col"
        >
          <h3 className="text-base font-bold text-khiimori-text-primary mb-3 flex items-center gap-2">
            <Activity className="w-4 h-4 text-purple-400" />
            Productivity Trend
          </h3>
          {chartMetrics.length === 0 || chartMetrics.every((m) => m.score === 0) ? (
            <div className="flex-1 min-h-[200px] flex items-center justify-center text-sm text-khiimori-text-muted">
              No activity in this period yet.
            </div>
          ) : (
            <div className="flex items-end gap-1.5 flex-1" style={{ minHeight: CHART_HEIGHT_PX }}>
              {chartMetrics.map((metric, index) => {
                const barHeight = metric.score > 0
                  ? Math.max(8, (metric.score / maxScore) * (CHART_HEIGHT_PX - 48))
                  : 2;
                return (
                  <div
                    key={`${metric.date}-${index}`}
                    className="flex-1 flex flex-col items-center justify-end h-full min-w-0"
                  >
                    <motion.div
                      initial={{ height: 0 }}
                      animate={{ height: barHeight }}
                      transition={{ delay: index * 0.04, duration: 0.4 }}
                      className={`w-full ${getScoreBarClass(metric.score)} rounded-t-md`}
                    />
                    <span className="text-[10px] text-khiimori-text-muted truncate w-full text-center mt-1.5">
                      {metric.label}
                    </span>
                    <span className={`text-[10px] font-bold ${getScoreColor(metric.score)}`}>
                      {metric.score}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: 12 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.05 }}
          className="bg-khiimori-bg-elevated/5 backdrop-blur-xl rounded-xl shadow-md p-5 border border-khiimori-border/10 flex flex-col"
        >
          <h3 className="text-base font-bold text-khiimori-text-primary mb-3 flex items-center gap-2">
            <Clock className="w-4 h-4 text-primary-400" />
            Focus Time Breakdown
            {activeFocusDays.length > 0 ? (
              <span className="text-xs font-normal text-khiimori-text-muted">
                {activeFocusDays.length} active {activeFocusDays.length === 1 ? 'day' : 'days'}
              </span>
            ) : null}
          </h3>
          {activeFocusDays.length === 0 ? (
            <div className="flex-1 min-h-[200px] flex items-center justify-center text-sm text-khiimori-text-muted">
              No focus time recorded for this period.
            </div>
          ) : (
            <div className="space-y-2.5 flex-1">
              {focusChartMetrics.map((metric, index) => (
                <div key={`${metric.date}-focus-${index}`} className="flex items-center gap-3">
                  <div className="w-14 shrink-0 text-xs text-khiimori-text-secondary truncate">
                    {metric.label}
                  </div>
                  <div className="flex-1 h-6 bg-khiimori-bg-elevated/10 rounded-full overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{
                        width: `${Math.min(100, (metric.focusSeconds / maxFocusSeconds) * 100)}%`,
                      }}
                      transition={{ delay: index * 0.04 }}
                      className="h-full bg-gradient-primary rounded-full"
                    />
                  </div>
                  <div className="w-14 shrink-0 text-right text-xs font-medium text-khiimori-text-primary">
                    {formatDurationFromSeconds(metric.focusSeconds)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </motion.div>
      </div>

      {/* Trends — compact full-width strip */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-khiimori-bg-elevated/5 backdrop-blur-xl rounded-xl shadow-md p-5 border border-khiimori-border/10"
      >
        <h3 className="text-base font-bold text-khiimori-text-primary mb-3 flex items-center gap-2">
          <BarChart3 className="w-4 h-4 text-success-400" />
          Trends
          <span className="text-xs font-normal text-khiimori-text-muted">vs prior period</span>
        </h3>
        {trends.length === 0 ? (
          <p className="text-sm text-khiimori-text-muted">Not enough history to compare periods yet.</p>
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {trends.map((trend) => (
              <div key={trend.label} className="p-3 bg-khiimori-bg-elevated/10 rounded-lg">
                <div className="flex items-center gap-1.5 mb-1">
                  {trendIcon(trend)}
                  <span className="text-xs text-khiimori-text-secondary truncate">{trend.label}</span>
                </div>
                <div
                  className={`text-xl font-bold ${
                    trend.change === 0
                      ? 'text-khiimori-text-muted'
                      : trend.up
                        ? 'text-success-400'
                        : 'text-red-400'
                  }`}
                >
                  {trend.change === 0 ? '0%' : `${trend.up ? '+' : '-'}${trend.change}%`}
                </div>
              </div>
            ))}
          </div>
        )}
      </motion.div>
    </div>
  );
};
